import React, { useMemo, useState, useEffect } from 'react'
import { ChevronRight, ChevronDown, RefreshCw, CheckCircle, Clock } from 'lucide-react'
import TeamLogo from './TeamLogo'
import StandingsTab from './StandingsTab'
import LeadersTab from './TrendsTab'
import { usePrefetch, usePulse } from '../hooks/useGames'

function getGameDateLabel(game) {
  try {
    const rawStatus = (game?.raw?.status || game?.status || '').toString()
    const statusMatch = rawStatus.match(/^(\d{1,2})\/(\d{1,2})\s*-/)
    if (statusMatch) {
      const mm = statusMatch[1].padStart(2, '0')
      const dd = statusMatch[2].padStart(2, '0')
      return `${mm}/${dd}`
    }
    const dateValue = game?.raw?.date || game?.date || null
    if (!dateValue) return null
    const d = new Date(dateValue)
    if (Number.isNaN(d.getTime())) return null
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${mm}/${dd}`
  } catch {
    return null
  }
}

function formatStatus(game) {
  if (!game) return { text: '', isLive: false, isUpcoming: false }
  
  const status = (game.status || '').toUpperCase()
  const time = game.time || ''
  const rawStatus = game.status || ''
  const timeStr = (time || '').toLowerCase()
  const statusStr = (game.status || '').toLowerCase()

  const getNBAOvertimeLabel = (period) => {
    if (!Number.isFinite(period) || period < 5) return 'OT'
    return period === 5 ? 'OT' : `${period - 4}OT`
  }
  const parseNBAPeriod = () => {
    // Prefer explicit numeric period if present
    if (Number.isFinite(game.period) && game.period > 0) return game.period
    // Common shapes: "Q4", "QTR 4", "Qtr 4"
    const qMatch = status.match(/\bQ(?:TR)?\s*(\d+)\b/i) || rawStatus.match(/\bQ(?:TR)?\s*(\d+)\b/i)
    if (qMatch?.[1]) return parseInt(qMatch[1], 10)
    // Sometimes time contains quarter label
    const tMatch = time.match(/\bQ\s*(\d+)\b/i) || time.match(/\b(\d)(st|nd|rd|th)\s*qtr\b/i)
    if (tMatch?.[1]) return parseInt(tMatch[1], 10)
    // OT without explicit quarter number
    if (status.includes('OT') || timeStr.includes('ot') || statusStr.includes('ot')) return 5
    return null
  }
  
  // FINAL game - check first
  if (status.includes('FINAL') || status.includes('END')) {
    // Check if game went to OT
    // NBA: period >= 5 means overtime
    // NFL: home_team_ot exists means overtime
    const period = game.period || 0
    const hasNBAOT = period >= 5
    const hasNFLOT = game.home_team_ot != null || game.visitor_team_ot != null
    const hasOT = hasNBAOT || hasNFLOT
    return { text: hasOT ? 'FINAL/OT' : 'FINAL', isLive: false, isUpcoming: false }
  }
  
  // NFL LIVE game - format like "9:35 - 2nd" or "12:00 - 1st"
  // Match pattern: time - quarter (e.g., "9:35 - 2nd", "0:45 - 4th")
  const nflLiveMatch = rawStatus.match(/^(\d{1,2}:\d{2})\s*-\s*(1st|2nd|3rd|4th|OT)/i)
  if (nflLiveMatch) {
    const [, clock, quarter] = nflLiveMatch
    // 0:00 in 4th quarter = game is over, show FINAL
    if (clock === '0:00' && quarter.toLowerCase() === '4th') {
      return { text: 'FINAL', isLive: false, isUpcoming: false }
    }
    return { text: `${quarter.toUpperCase()} • ${clock}`, isLive: true, isUpcoming: false }
  }
  
  // NFL Halftime
  if (status.includes('HALFTIME') || status === 'HALF') {
    return { text: 'HALFTIME', isLive: true, isUpcoming: false, isBreak: true }
  }
  
  // NFL End of quarter (0:00 in 1st, 2nd, 3rd)
  const nflEndQtrMatch = rawStatus.match(/^0:00\s*-\s*(1st|2nd|3rd)/i)
  if (nflEndQtrMatch) {
    const [, quarter] = nflEndQtrMatch
    if (quarter.toLowerCase() === '2nd') {
      return { text: 'HALFTIME', isLive: true, isUpcoming: false, isBreak: true }
    }
    return { text: `END ${quarter.toUpperCase()}`, isLive: true, isUpcoming: false, isBreak: true }
  }
  
  // NBA LIVE game - check multiple indicators
  if (status === 'LIVE' || status.includes('QTR') || status.includes('HALF') ||
      status.includes('IN PROGRESS') || status.includes('INNING') ||
      (status.includes('Q') && !status.includes('Q0'))) {
    // Check if game is actually over - multiple ways to detect:
    // 1. Q4 with 0:00 in time field
    // 2. Q4 with "End" or "Final" in time
    // 3. Status is exactly "Q4" with no time or just "Q4" (game ended, API hasn't updated to FINAL yet)
    // 4. Time shows just "Q4" with no clock (means Q4 ended)
    const isQ4 = status.includes('4') || status.includes('Q4') || timeStr.includes('4th') || timeStr.includes('q4')
    const timeIsZero = timeStr.includes('0:00') || timeStr === '0:00' || time === '0:00'
    const timeIsEnd = timeStr.includes('end') || timeStr.includes('final')
    // If time is just "Q4" or "4th Qtr" without a clock, game is over
    const timeIsJustQ4 = time === 'Q4' || time === '4th Qtr' || time === '4th' || timeStr === 'q4'
    const noTimeInQ4 = isQ4 && (!time || timeIsJustQ4 || !time.includes(':'))
    
    if (isQ4 && (timeIsZero || timeIsEnd || noTimeInQ4)) {
      return { text: 'FINAL', isLive: false, isUpcoming: false }
    }
    
    // NBA Halftime detection
    if (status.includes('HALF') || timeStr.includes('half')) {
      return { text: 'HALFTIME', isLive: true, isUpcoming: false, isBreak: true }
    }
    
    // NBA End of quarter (Q1, Q2, Q3 with 0:00 or "End")
    const isQ1 = status.includes('1') || status.includes('Q1') || timeStr.includes('1st')
    const isQ2 = status.includes('2') || status.includes('Q2') || timeStr.includes('2nd')
    const isQ3 = status.includes('3') || status.includes('Q3') || timeStr.includes('3rd')
    if ((isQ1 || isQ2 || isQ3) && (timeIsZero || timeIsEnd)) {
      if (isQ2) {
        return { text: 'HALFTIME', isLive: true, isUpcoming: false, isBreak: true }
      }
      const qtr = isQ1 ? '1ST' : isQ3 ? '3RD' : ''
      if (qtr) {
        return { text: `END ${qtr}`, isLive: true, isUpcoming: false, isBreak: true }
      }
    }

    // NBA Overtime handling (avoid showing "5th quarter" style labels)
    const period = parseNBAPeriod()
    if (period && period >= 5) {
      const otLabel = getNBAOvertimeLabel(period)
      // End of OT / between OTs: show break label instead of "FINAL" until API updates
      if (timeIsZero || timeIsEnd) {
        return { text: `END ${otLabel}`, isLive: true, isUpcoming: false, isBreak: true }
      }
      // Prefer explicit clock if present; otherwise show OT label
      const clockMatch = time.match(/\d{1,2}:\d{2}/)
      const clock = clockMatch?.[0] || ''
      return { text: clock ? `${otLabel} • ${clock}` : otLabel, isLive: true, isUpcoming: false }
    }
    
    // Show time like "2nd Qtr" or "Q3 5:42"
    return { text: time || 'LIVE', isLive: true, isUpcoming: false }
  }
  
  // UPCOMING game - check for scheduled status or time patterns like "8:15 PM EST"
  const timePattern = /\d{1,2}:\d{2}\s*(AM|PM)/i
  if (status.includes('SCHEDULED') || status.includes('UPCOMING') || 
      status === 'Q0 0-0' || status.includes('Q0') || !status ||
      timePattern.test(time) || timePattern.test(rawStatus)) {
    // For upcoming games, show time if available, otherwise extract from startTime
    let displayText = time
    if (!displayText) {
      // Try to get day of week and time from game date
      try {
        const dateStr = game.startTime || game.raw?.startTime || game.startsAt || game.date || game.raw?.date
        if (dateStr && typeof dateStr === 'string' && dateStr.length > 0) {
          const gameDate = new Date(dateStr)
          // Validate the date is valid and in the future (sanity check)
          if (!Number.isNaN(gameDate.getTime()) && gameDate.getTime() > Date.now() - 86400000) {
            const dayName = gameDate.toLocaleDateString('en-US', { weekday: 'short' })
            const timeStr = gameDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
            // Validate formatting worked
            if (dayName && timeStr && dayName !== 'Invalid Date' && !timeStr.includes('Invalid')) {
              displayText = `${dayName} ${timeStr}`
            }
          }
        }
      } catch (err) {
        // Log error for debugging but don't break
        console.warn('[formatStatus] Failed to parse game date:', { dateStr: game.startTime, error: err.message })
      }
    }
    return { text: displayText || 'TBD', isLive: false, isUpcoming: true }
  }
  
  return { text: time || status, isLive: false, isUpcoming: false }
}

// Official NBA/NFL/MLB abbreviations (TV style)
const TEAM_ABBREVS = {
  // NBA
  '76ers': 'PHI', 'sixers': 'PHI', 'philadelphia': 'PHI',
  'celtics': 'BOS', 'boston': 'BOS',
  'nets': 'BKN', 'brooklyn': 'BKN',
  'knicks': 'NYK', 'new york knicks': 'NYK',
  'raptors': 'TOR', 'toronto': 'TOR',
  'bulls': 'CHI', 'chicago': 'CHI',
  'cavaliers': 'CLE', 'cavs': 'CLE', 'cleveland': 'CLE',
  'pistons': 'DET', 'detroit': 'DET',
  'pacers': 'IND', 'indiana': 'IND',
  'bucks': 'MIL', 'milwaukee': 'MIL',
  'hawks': 'ATL', 'atlanta': 'ATL',
  'hornets': 'CHA', 'charlotte': 'CHA',
  'heat': 'MIA', 'miami': 'MIA',
  'magic': 'ORL', 'orlando': 'ORL',
  'wizards': 'WAS', 'washington': 'WAS',
  'nuggets': 'DEN', 'denver': 'DEN',
  'timberwolves': 'MIN', 'wolves': 'MIN', 'minnesota': 'MIN',
  'thunder': 'OKC', 'oklahoma': 'OKC', 'oklahoma city': 'OKC',
  'trail blazers': 'POR', 'blazers': 'POR', 'portland': 'POR',
  'jazz': 'UTA', 'utah': 'UTA',
  'warriors': 'GSW', 'golden state': 'GSW',
  'clippers': 'LAC', 'la clippers': 'LAC',
  'lakers': 'LAL', 'la lakers': 'LAL', 'los angeles lakers': 'LAL',
  'suns': 'PHX', 'phoenix': 'PHX',
  'kings': 'SAC', 'sacramento': 'SAC',
  'mavericks': 'DAL', 'mavs': 'DAL', 'dallas': 'DAL',
  'rockets': 'HOU', 'houston': 'HOU',
  'grizzlies': 'MEM', 'memphis': 'MEM',
  'pelicans': 'NOP', 'new orleans': 'NOP',
  'spurs': 'SAS', 'san antonio': 'SAS',
  // NFL
  'chiefs': 'KC', 'kansas city': 'KC',
  'ravens': 'BAL', 'baltimore': 'BAL',
  'bills': 'BUF', 'buffalo': 'BUF',
  'dolphins': 'MIA', 'bengals': 'CIN', 'cincinnati': 'CIN',
  'browns': 'CLE', 'steelers': 'PIT', 'pittsburgh': 'PIT',
  'texans': 'HOU', 'colts': 'IND', 'jaguars': 'JAX', 'jacksonville': 'JAX',
  'titans': 'TEN', 'tennessee': 'TEN',
  'broncos': 'DEN', 'chargers': 'LAC', 'raiders': 'LV', 'las vegas': 'LV',
  'cowboys': 'DAL', 'giants': 'NYG', 'new york giants': 'NYG',
  'eagles': 'PHI', 'commanders': 'WAS',
  'bears': 'CHI', 'lions': 'DET', 'packers': 'GB', 'green bay': 'GB',
  'vikings': 'MIN', 'falcons': 'ATL', 'panthers': 'CAR', 'carolina': 'CAR',
  'saints': 'NO', 'buccaneers': 'TB', 'bucs': 'TB', 'tampa bay': 'TB',
  'cardinals': 'ARI', 'arizona': 'ARI', 'rams': 'LAR', 'la rams': 'LAR',
  '49ers': 'SF', 'niners': 'SF', 'san francisco': 'SF', 'seahawks': 'SEA', 'seattle': 'SEA',
}

function getTeamAbbrev(team) {
  if (!team) return '???'
  // Check if team already has a proper abbreviation
  const abbr = team.abbreviation || team.code || team.abbrev
  if (abbr && abbr.length <= 3) return abbr.toUpperCase()
  
  // Look up by name
  const name = (team.name || team.nickname || '').toLowerCase()
  for (const [key, val] of Object.entries(TEAM_ABBREVS)) {
    if (name.includes(key)) return val
  }
  
  // Fallback: first 3 chars of name
  return (team.name || team.nickname || '???').slice(0, 3).toUpperCase()
}

function formatTeamName(team, fallback) {
  if (!team) return fallback
  // Extract team name only (no city) - match NBA style
  const fullName = team.name || team.nickname || team.code || fallback
  
  // For NFL teams with city names (e.g., "New York Giants"), extract just the team name
  // Split by space and take the last word(s) that form the team name
  const parts = fullName.split(' ')
  
  // Common NFL city prefixes to remove
  const cityPrefixes = ['New', 'San', 'Los', 'Kansas']
  let teamName = fullName
  
  // If first word is a city prefix, skip it and the next word (city name)
  if (cityPrefixes.includes(parts[0]) && parts.length > 1) {
    teamName = parts.slice(2).join(' ') || fullName
  }
  
  return teamName.length > 12 ? teamName.slice(0, 10) + '…' : teamName
}

function formatScore(team) {
  if (!team) return '—'
  if (team.score != null) return team.score
  if (team.points != null) return team.points
  return '—'
}

function getNFLGameDayLabel(game) {
  try {
    const league = (game?.league || '').toString().toLowerCase()
    if (league !== 'nfl') return null

    const rawStatus = (game?.raw?.status || game?.status || '').toString()
    const statusMatch = rawStatus.match(/^(\d{1,2})\/(\d{1,2})\s*-/)
    if (statusMatch) {
      const month = parseInt(statusMatch[1], 10)
      const day = parseInt(statusMatch[2], 10)
      const year = new Date().getFullYear()
      const d = new Date(year, month - 1, day)
      return d.toLocaleDateString('en-US', { weekday: 'short' })
    }

    const dateValue = game?.raw?.date || game?.date || null
    if (!dateValue) return null
    const d = new Date(dateValue)
    if (Number.isNaN(d.getTime())) return null
    return d.toLocaleDateString('en-US', { weekday: 'short' })
  } catch {
    return null
  }
}

// TeamBadge wrapper for game cards
function TeamBadge({ name, code, teamId, league }) {
  return <TeamLogo name={name} code={code} teamId={teamId} league={league} size={28} />
}

// Premium game card - App Store ready
function GameCard({ game, isSelected, onSelect, onAskAI, selectedBook = 'dk', onBookToggle }) {
  const homeTeam = game?.teams?.[0] || {}
  const awayTeam = game?.teams?.[1] || {}
  const status = formatStatus(game)
  const liveSituation = game?.situation || game?.raw?.situation || null
  const { prefetchGamePreview } = usePrefetch()
  const gameLeague = (game?.league || game?.raw?.league || 'nba').toString().toLowerCase()

  const nflDayLabel = getNFLGameDayLabel(game)
  const dateLabel = getGameDateLabel(game)
  
  const homeScore = parseInt(formatScore(homeTeam)) || 0
  const awayScore = parseInt(formatScore(awayTeam)) || 0
  const homeWinning = homeScore > awayScore
  const awayWinning = awayScore > homeScore
  
  // Get game ID for prefetching
  const gameId = game?.raw?.id || game?.id

  return (
    <div
      onClick={() => onSelect(game)}
      onMouseEnter={() => prefetchGamePreview(gameId, gameLeague)}
      onTouchStart={() => prefetchGamePreview(gameId, gameLeague)}
      className="btn-glide tap-feedback"
      style={{
        background: isSelected ? 'linear-gradient(135deg, rgba(255,77,109,0.08) 0%, rgba(255,77,109,0.02) 100%)' : '#fff',
        borderRadius: '14px',
        padding: '14px',
        border: isSelected ? '2px solid #FF4D6D' : '1px solid #E5E7EB',
        cursor: 'pointer',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      {/* Header: League + Date/Time + Status */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
            {game.league || 'NBA'}{nflDayLabel ? ` • ${nflDayLabel}` : ''}{dateLabel ? ` • ${dateLabel}` : ''}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {status.isLive && (
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-pink-500"></span>
            </span>
          )}
          <span className={`text-[11px] font-semibold ${status.isLive ? 'text-pink-500' : 'text-gray-500'}`}>
            {status.text}
          </span>
        </div>
      </div>

      {/* Teams + Scores/Records */}
      <div className="space-y-2">
        {/* Away team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-medium text-gray-400 w-6">AWY</span>
            <TeamBadge name={awayTeam.name} code={awayTeam.code} teamId={awayTeam.id} league={game.league} />
            <div className="flex items-center gap-1.5">
              <span className={`text-sm font-medium ${awayWinning ? 'text-gray-900' : 'text-gray-600'}`}>
                {formatTeamName(awayTeam, 'Away')}
              </span>
              {(awayTeam.record || game.standings?.away?.record) && (
                <span className="text-[10px] text-gray-400">({awayTeam.record || game.standings?.away?.record})</span>
              )}
            </div>
          </div>
          <span className={`text-lg font-score ${awayWinning ? 'text-gray-900' : 'text-gray-400'}`}>
            {!status.isUpcoming ? formatScore(awayTeam) : '—'}
          </span>
        </div>
        
        {/* Home team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-medium text-gray-400 w-6">HME</span>
            <TeamBadge name={homeTeam.name} code={homeTeam.code} teamId={homeTeam.id} league={game.league} />
            <div className="flex items-center gap-1.5">
              <span className={`text-sm font-medium ${homeWinning ? 'text-gray-900' : 'text-gray-600'}`}>
                {formatTeamName(homeTeam, 'Home')}
              </span>
              {(homeTeam.record || game.standings?.home?.record) && (
                <span className="text-[10px] text-gray-400">({homeTeam.record || game.standings?.home?.record})</span>
              )}
            </div>
          </div>
          <span className={`text-lg font-score ${homeWinning ? 'text-gray-900' : 'text-gray-400'}`}>
            {!status.isUpcoming ? formatScore(homeTeam) : '—'}
          </span>
        </div>
      </div>

      {/* NFL Live Situation - down, distance, possession */}
      {liveSituation && (
        <div className="mt-1 py-1 px-2 bg-gray-50 rounded flex items-center justify-center gap-1.5">
          {liveSituation.down === -1 ? (
            <span className="text-[10px] font-medium text-gray-500">Break</span>
          ) : (
            <>
              {liveSituation.possession && (
                <span className="text-[10px] font-semibold text-pink-600">{liveSituation.possession} ball</span>
              )}
              {liveSituation.possession && liveSituation.downDistanceText && <span className="text-gray-300">•</span>}
              {liveSituation.downDistanceText && (
                <span className="text-[10px] font-medium text-gray-600">{liveSituation.downDistanceText}</span>
              )}
              {!liveSituation.downDistanceText && liveSituation.possessionText && (
                <span className="text-[10px] font-medium text-gray-600">{liveSituation.possessionText}</span>
              )}
            </>
          )}
        </div>
      )}

      {/* Odds - with book toggle (hide for final games) */}
      {game.odds && !status.text?.toLowerCase().includes('final') ? (() => {
        // Get odds from selected book or default
        const books = game.odds.books || {}
        const hasBothBooks = books.dk && books.fd
        const activeOdds = selectedBook === 'fd' && books.fd ? books.fd : (books.dk || game.odds)
        
        // Parse moneylines as numbers for comparison (they may be strings like "+220" or "-275")
        const parseML = (ml) => typeof ml === 'string' ? parseFloat(ml.replace('+', '')) : (ml || 0)
        const homeML = parseML(activeOdds.moneyline?.home) || parseML(game.odds.moneyline_home_odds)
        const awayML = parseML(activeOdds.moneyline?.away) || parseML(game.odds.moneyline_away_odds)
        const homeIsFav = homeML < awayML
        const favTeamObj = homeIsFav ? homeTeam : awayTeam
        const dogTeamObj = homeIsFav ? awayTeam : homeTeam
        const favAbbrev = getTeamAbbrev(favTeamObj)
        const dogAbbrev = getTeamAbbrev(dogTeamObj)
        const favOdds = homeIsFav 
          ? (activeOdds.moneyline?.home || game.odds.moneyline_home_odds) 
          : (activeOdds.moneyline?.away || game.odds.moneyline_away_odds)
        const dogOdds = homeIsFav 
          ? (activeOdds.moneyline?.away || game.odds.moneyline_away_odds) 
          : (activeOdds.moneyline?.home || game.odds.moneyline_home_odds)
        
        // Determine line label based on game state
        const isGameLive = status.isLive
        const isFinal = status.text === 'FINAL'
        const lineLabel = isFinal ? 'Closing Lines' : isGameLive ? 'Live Lines' : 'Current Lines'
        
        return (
          <div className="mt-3 pt-3 border-t border-gray-100">
            {/* Only show label row for live games with actual line values */}
            {isGameLive && (activeOdds.spread || game.odds.spread_home_value || activeOdds.total || game.odds.total_value) && (
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[8px] text-gray-400 uppercase tracking-wide">
                  {lineLabel}
                </span>
                <span className="text-[8px] text-pink-500 font-medium flex items-center gap-0.5">
                  <span className="w-1.5 h-1.5 bg-pink-500 rounded-full animate-pulse"></span>
                  Live
                </span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <div className="flex-1 bg-gray-50 rounded-lg px-2 py-1.5 text-center">
                <span className="text-[8px] text-gray-400 block uppercase">Spread</span>
                <span className="text-[11px] font-bold text-gray-800">{activeOdds.spread || game.odds.spread_home_value || '-'}</span>
              </div>
              <div className="flex-1 bg-gray-50 rounded-lg px-2 py-1.5 text-center">
                <span className="text-[8px] text-gray-400 block uppercase">O/U</span>
                <span className="text-[11px] font-bold text-gray-800">{activeOdds.total || game.odds.total_value || '-'}</span>
              </div>
              <div className="flex-1 bg-green-50 rounded-lg px-1.5 py-1.5 text-center border border-green-100">
                <span className="text-[7px] text-green-600 block uppercase" title={formatTeamName(favTeamObj, 'Fav')}>{favAbbrev}</span>
                <span className="text-[11px] font-bold text-green-700">{favOdds || '-'}</span>
              </div>
              <div className="flex-1 bg-orange-50 rounded-lg px-1.5 py-1.5 text-center border border-orange-100">
                <span className="text-[7px] text-orange-600 block uppercase" title={formatTeamName(dogTeamObj, 'Dog')}>{dogAbbrev}</span>
                <span className="text-[11px] font-bold text-orange-700">+{Math.abs(dogOdds || 0)}</span>
              </div>
              {/* Sportsbook toggle - tap to switch between DK/FD */}
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  if (hasBothBooks) {
                    onBookToggle?.(selectedBook === 'dk' ? 'fd' : 'dk')
                  }
                }}
                className={`text-[8px] font-semibold px-1.5 py-0.5 rounded transition-colors ${
                  hasBothBooks 
                    ? 'bg-gray-900 text-white hover:bg-gray-700 cursor-pointer' 
                    : 'bg-gray-100 text-gray-500 cursor-default'
                }`}
                title={hasBothBooks ? 'Tap to switch books' : `Only ${selectedBook.toUpperCase()} available`}
                style={{ minWidth: '24px' }}
              >
                {selectedBook.toUpperCase()}
              </button>
            </div>
          </div>
        )
      })() : game.oddsStatus && !status.isLive && !status.text?.toLowerCase().includes('final') && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-center gap-2 py-1.5">
            {game.oddsStatus === 'pending' && (
              <>
                <RefreshCw className="w-3 h-3 text-amber-500 animate-spin" style={{ animationDuration: '3s' }} />
                <span className="text-[11px] text-amber-600 font-medium">Awaiting book...</span>
              </>
            )}
            {game.oddsStatus === 'not_posted' && (
              <span className="text-[11px] text-gray-400">Lines not yet posted</span>
            )}
          </div>
        </div>
      )}

      {/* Quick action - context-aware based on game status */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          const isFinal = status.text?.toLowerCase().includes('final')
          // Clean, natural prompts without robotic brackets
          const prompt = status.isLive 
            ? `What's happening in the ${awayTeam.name} vs ${homeTeam.name} game right now?`
            : isFinal 
              ? `Recap the ${awayTeam.name} vs ${homeTeam.name} game - who won, top performers, and did the spread/over hit?`
              : `Preview ${awayTeam.name} at ${homeTeam.name} - key matchups and betting angles`
          // Pass gameId + game context so backend has all data for cleaner output
          onAskAI?.({ 
            question: prompt, 
            gameId: game.id,
            title: `${awayTeam.name} @ ${homeTeam.name}`,
            // Pass game data for server-side context injection
            game: {
              id: game.id,
              homeTeam: homeTeam.name,
              awayTeam: awayTeam.name,
              homeRecord: game.standings?.home?.record,
              awayRecord: game.standings?.away?.record,
              odds: game.odds,
              status: status.text,
              venue: game.raw?.venue || game.venue,
              date: game.raw?.date || game.date,
            }
          })
        }}
        className="mt-3 w-full py-2 text-xs font-semibold rounded-lg transition-all hover:bg-pink-50 active:scale-[0.98]"
        style={{ 
          color: status.isLive ? '#FF4D6D' : '#6B7280', 
          background: status.isLive ? 'rgba(255,77,109,0.08)' : '#F9FAFB',
          border: 'none'
        }}
      >
        {status.isLive ? 'Get Live Insights' : status.text?.toLowerCase().includes('final') ? 'Game Recap' : 'Preview'}
      </button>
    </div>
  )
}

// Compact Injuries Block for Home view
function InjuriesBlock({ league, onAskAI }) {
  const { data: pulseData } = usePulse(league)
  const [expanded, setExpanded] = useState(false)
  
  const injuries = useMemo(() => {
    const items = pulseData?.items || []
    return items
      .filter(i => (i.type === 'injury' || i.type === 'status_change'))
      .filter(i => {
        const itemLeague = (i.league || 'nba').toLowerCase()
        return itemLeague === league
      })
      .slice(0, expanded ? 10 : 4)
  }, [pulseData, league, expanded])
  
  const totalCount = useMemo(() => {
    const items = pulseData?.items || []
    return items.filter(i => (i.type === 'injury' || i.type === 'status_change')).length
  }, [pulseData])
  
  if (injuries.length === 0) return null
  
  const getStatusBadge = (item) => {
    const status = (item.description || item.title || '').toLowerCase()
    if (status.includes('out') || status.includes('ruled out')) return { label: 'OUT', color: 'bg-red-100 text-red-600' }
    if (status.includes('questionable') || status.includes('gtd')) return { label: 'GTD', color: 'bg-yellow-100 text-yellow-700' }
    if (status.includes('doubtful')) return { label: 'DBT', color: 'bg-orange-100 text-orange-700' }
    if (status.includes('probable')) return { label: 'PROB', color: 'bg-green-100 text-green-600' }
    if (status.includes('cleared') || status.includes('available')) return { label: 'OK', color: 'bg-green-100 text-green-600' }
    if (status.includes('day-to-day') || status.includes('dtd')) return { label: 'DTD', color: 'bg-amber-100 text-amber-700' }
    return { label: 'INJ', color: 'bg-gray-100 text-gray-600' }
  }
  
  return (
    <div 
      className="mx-1 mb-3 bg-white"
      style={{ borderRadius: '14px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
        style={{ borderRadius: expanded ? '14px 14px 0 0' : '14px' }}
      >
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide">Injuries & Availability</span>
          <span className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{totalCount}</span>
        </div>
        <ChevronDown 
          size={14} 
          className={`text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {/* Injury list */}
      <div className="divide-y divide-gray-50">
        {injuries.map((item, idx) => {
          const badge = getStatusBadge(item)
          const playerName = item.player || item.title?.split(' - ')[0] || 'Unknown'
          const team = item.team || ''
          
          return (
            <div 
              key={item.id || idx}
              className="px-3 py-2 hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => onAskAI?.({ question: `What's the latest on ${playerName}?`, title: playerName })}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[12px] font-medium text-gray-800 truncate">{playerName}</span>
                    {team && <span className="text-[10px] text-gray-400">{team}</span>}
                  </div>
                  <p className="text-[10px] text-gray-400 truncate mt-0.5">
                    {item.description || item.title?.split(' - ')[1] || ''}
                  </p>
                </div>
                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${badge.color}`}>
                  {badge.label}
                </span>
              </div>
            </div>
          )
        })}
      </div>
      
      {/* Show more */}
      {totalCount > 4 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full px-3 py-2 text-[10px] font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors border-t border-gray-50"
          style={{ borderRadius: '0 0 14px 14px' }}
        >
          {expanded ? 'Show less' : `Show ${totalCount - 4} more`}
        </button>
      )}
    </div>
  )
}

export default function GamesTab({
  games = [],
  selectedGame,
  onSelectGame,
  onAskAI,
  isLoading,
  error,
  onRefresh,
  selectedDate = 'today',
  onDateChange,
  selectedLeague = 'nba',
  onLeagueChange,
  lastPoll,
  dataStatus
}) {
  // Calculate data freshness
  const pollAge = lastPoll ? Math.round((Date.now() - new Date(lastPoll).getTime()) / 1000) : null
  const isStale = pollAge && pollAge > 120 // 2 minutes
  const isHealthy = pollAge && pollAge < 120
  const [pagerIndex, setPagerIndex] = useState(0) // 0 = Games, 1 = Standings, 2 = Leaders
  const [bookSelections, setBookSelections] = useState({}) // per-game DK/FD toggle
  
  // Use selectedLeague from props
  const selectedSport = selectedLeague || 'nba'
  
  // Reset pager when Leaders isn't available
  useEffect(() => {
    // Reset when on Leaders and switching to NBA
    if (selectedSport === 'nba' && pagerIndex === 2) {
      setPagerIndex(0)
    }
  }, [selectedSport])
  
  // Don't prefetch all previews on load - causes 500 errors
  // Previews prefetch on hover/touch instead (see GameCard onMouseEnter/onTouchStart)

  const getSelectedBookForGame = (gameId) => bookSelections[gameId] || 'dk'
  const handleBookToggle = (gameId, nextBook) => {
    setBookSelections(prev => ({ ...prev, [gameId]: nextBook }))
  }

  const filteredGames = useMemo(() => {
    // No filtering needed - games come pre-filtered by league from the API
    const filtered = games.filter((game) => game != null)
    
    // Sort: LIVE games first, then by status
    return filtered.sort((a, b) => {
      const aStatus = formatStatus(a)
      const bStatus = formatStatus(b)
      
      // Live games always first
      if (aStatus.isLive && !bStatus.isLive) return -1
      if (!aStatus.isLive && bStatus.isLive) return 1
      
      // Then upcoming games
      if (aStatus.isUpcoming && !bStatus.isUpcoming && !bStatus.isLive) return -1
      if (!aStatus.isUpcoming && bStatus.isUpcoming && !aStatus.isLive) return 1
      
      return 0
    })
  }, [games, selectedSport])

  const isSportSelected = selectedSport !== 'all'
  
  const setSelectedSport = (sport) => {
    if (onLeagueChange) {
      onLeagueChange(sport)
    }
  }
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Home tabs: Games/Standings/Leaders */}
      {isSportSelected && (
        <div className="px-3 pb-1 flex justify-center">
          <div className="inline-flex border-b border-gray-200">
            {['Games', 'Standings', 'Leaders'].map((tab, idx) => {
              const isActive = pagerIndex === idx
              // Only show Leaders for NFL (has data), disable for NBA
              const isDisabled = tab === 'Leaders' && selectedSport === 'nba'
              return (
                <button
                  key={tab}
                  onClick={() => !isDisabled && setPagerIndex(idx)}
                  disabled={isDisabled}
                  className="transition-all duration-150"
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: isDisabled ? '#D1D5DB' : isActive ? '#FF4D6D' : '#9CA3AF',
                    borderBottom: isActive ? '2px solid #FF4D6D' : '2px solid transparent',
                    marginBottom: '-1px',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                  }}
                  title={isDisabled ? 'Coming soon for NBA' : ''}
                >
                  {tab}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Content area - Games, Standings, or Leaders based on pager */}
      <div style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
        {/* Standings view */}
        {pagerIndex === 1 && isSportSelected && (
          <StandingsTab league={selectedSport} />
        )}
        
        {/* Leaders view */}
        {pagerIndex === 2 && isSportSelected && (
          <LeadersTab league={selectedSport} />
        )}
        
        {/* Games view (default) */}
        {(pagerIndex === 0 || !isSportSelected) && (
          <div style={{ padding: '10px 4px' }}>
            {isLoading && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[1, 2, 3].map(i => (
                  <div key={i} className="skeleton" style={{ 
                    height: '100px', 
                    borderRadius: '12px',
                    padding: '12px'
                  }}>
                    <div className="flex justify-between items-start">
                      <div className="skeleton-text" style={{ width: '60%', marginBottom: '8px' }} />
                      <div className="skeleton-text" style={{ width: '40px' }} />
                    </div>
                    <div className="flex justify-between mt-3">
                      <div className="skeleton-score" />
                      <div className="skeleton-score" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {error && !isLoading && (
              <div style={{
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: '#FEF2F2',
                color: '#DC2626',
                fontSize: '12px'
              }}>
                {error}
              </div>
            )}

            {!isLoading && !error && filteredGames.length === 0 && (
              <p style={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'center', padding: '20px 0' }}>
                No live games right now
              </p>
            )}

            <div className="stagger-children" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredGames.map((game) => (
                <GameCard
                  key={game.id}
                  game={game}
                  isSelected={selectedGame?.id === game.id}
                  onSelect={onSelectGame}
                  onAskAI={onAskAI}
                  selectedBook={getSelectedBookForGame(game.id)}
                  onBookToggle={(nextBook) => handleBookToggle(game.id, nextBook)}
                />
              ))}
            </div>
            
          </div>
        )}
        
      </div>
    </div>
  )
}
